import React, { useEffect } from "react";
import ReactGA from "react-ga4";

import styles from "./carverticalWidget.scss";

// Dedicated tracking URLs provided by CarVertical for the Index Oglasi pilot.
const BASE = "https://www.carvertical.deal/31SHT4K/964QF6/";
const NO_VIN_URL = `${BASE}?source_id=AFF&sub1=indexhr&sub2=novin`;
const WITH_VIN_TEMPLATE = `${BASE}?uid=167&source_id=AFF&sub1=indexhr&sub2=withvin&sub3=`;

// Title used by the BE detail config for the VIN row. Matched by string
// because item objects don't carry a stable slug — kept in one place so
// future BE label tweaks are a single-line change.
export const VIN_DETAIL_TITLE = "Broj šasije";

const GA_CATEGORY = "Carvertical widget";

export const buildCarverticalUrl = (vin?: string | null): string => {
  const trimmed = vin && vin.trim();
  if (!trimmed) return NO_VIN_URL;
  return `${WITH_VIN_TEMPLATE}${encodeURIComponent(trimmed)}`;
};

// Extracts the VIN string from an ad's `details` groups. Ads without VIN
// (or with an empty value) return undefined.
export const findVinInDetails = (details: any): string | undefined => {
  if (!Array.isArray(details)) return undefined;
  for (const group of details) {
    const items = group && Array.isArray(group.data) ? group.data : [];
    for (const item of items) {
      if (item && item.title === VIN_DETAIL_TITLE && item.value) {
        return String(item.value);
      }
    }
  }
  return undefined;
};

// Ad details deliver module/category as HR slugs on the details page
// (`auto-moto` / `osobni-automobili`) but as EN slugs on some other flows —
// match both so the widget renders regardless of the entry point.
const isPersonalCar = (module?: string, category?: string): boolean =>
  (module === "vehicles" || module === "auto-moto") &&
  (category === "car" || category === "osobni-automobili");

const trackWidgetShown = () => {
  ReactGA.event({
    category: GA_CATEGORY,
    action: "View",
    label: "Prikazan widget"
  });
};

const trackWidgetClick = () => {
  ReactGA.event({
    category: GA_CATEGORY,
    action: "Click",
    label: "Klik na widget"
  });
};

// Fired by CarverticalVinLink when it appears/gets clicked next to the VIN
// row — exported so the sibling component doesn't need a shared helper file.
export const trackCarverticalVinLinkShown = () => {
  ReactGA.event({
    category: GA_CATEGORY,
    action: "View",
    label: "Prikazana provjera uz VIN"
  });
};

export const trackCarverticalVinLinkClick = () => {
  ReactGA.event({
    category: GA_CATEGORY,
    action: "Click",
    label: "Klik na provjeru uz VIN"
  });
};

interface Props {
  module?: string;
  category?: string;
  details?: any;
}

const CarverticalWidget: React.FC<Props> = ({ module, category, details }) => {
  const shouldRender = isPersonalCar(module, category);

  useEffect(() => {
    if (!shouldRender) return;
    trackWidgetShown();
  }, [shouldRender]);

  if (!shouldRender) return null;

  const vin = findVinInDetails(details);
  const url = buildCarverticalUrl(vin);

  return (
    <div className={styles.widget}>
      <div className={styles.copy}>
        <div className={styles.title}>
          {"Provjerite povijest ovog vozila uz 20% popusta"}
        </div>
        <div className={styles.description}>
          {"Kilometraža, evidentirana oštećenja, servisna povijest i pravni status vozila."}
        </div>
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={trackWidgetClick}
        className={styles.cta}
      >
        {"Provjeri"}
      </a>
    </div>
  );
};

export default CarverticalWidget;



// WEBPACK FOOTER //
// ./src/components/CarverticalWidget/CarverticalWidget.tsx